import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, Card, CardContent, Divider, Grid, Stack, TextField, Typography } from '@mui/material';
import { api } from '../api/client.js';
import PageHeader from '../components/PageHeader.jsx';
import { formatApiError, useToast } from '../contexts/ToastContext.jsx';

const departments = ['Cutting', 'Stitching', 'Ironing', 'Packaging'];
const emptyCost = () => ({ labourCost: 0, materialCost: 0, machineCost: 0, electricityCost: 0, miscExpense: 0, wastagePercent: 0 });

export default function CostingPage() {
  const toast = useToast();
  const [form, setForm] = useState({
    rawMaterialCost: 0, overheads: 0, packagingCost: 0, administrativeExpenses: 0,
    departmentCosts: departments.map((name) => ({ name, ...emptyCost() }))
  });
  const calculate = useMutation({
    mutationFn: async () => (await api.post('/costing/calculate', form, { skipErrorToast: true })).data,
    onSuccess: () => toast.success('Cost calculated successfully'),
    onError: (error) => toast.error(formatApiError(error, 'Unable to calculate cost'))
  });
  const setMain = (name, value) => setForm((current) => ({ ...current, [name]: Number(value) || 0 }));
  const setDepartment = (index, name, value) => setForm((current) => ({
    ...current, departmentCosts: current.departmentCosts.map((item, itemIndex) =>
      itemIndex === index ? { ...item, [name]: Number(value) || 0 } : item)
  }));

  return (
    <>
      <PageHeader title="Product Costing" subtitle="Calculate true garment cost across every production stage" />
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, xl: 8 }}><Stack spacing={2}>
          <Card><CardContent><Typography variant="h6" mb={2}>Base costs</Typography><Grid container spacing={2}>
            {['rawMaterialCost', 'overheads', 'packagingCost', 'administrativeExpenses'].map((field) =>
              <Grid key={field} size={{ xs: 12, sm: 6 }}><TextField fullWidth type="number"
                label={field.replace(/([A-Z])/g, ' $1').replace(/^./, (value) => value.toUpperCase())}
                value={form[field]} onChange={(e) => setMain(field, e.target.value)} /></Grid>)}
          </Grid></CardContent></Card>
          {form.departmentCosts.map((department, index) => <Card key={department.name}><CardContent>
            <Typography variant="h6" mb={2}>{department.name}</Typography>
            <Grid container spacing={2}>{Object.keys(emptyCost()).map((field) => <Grid key={field} size={{ xs: 6, md: 4 }}>
              <TextField fullWidth size="small" type="number" label={field.replace(/([A-Z])/g, ' $1')}
                value={department[field]} onChange={(e) => setDepartment(index, field, e.target.value)} />
            </Grid>)}</Grid>
          </CardContent></Card>)}
          <Button size="large" variant="contained" onClick={() => calculate.mutate()} disabled={calculate.isPending}>Calculate net product cost</Button>
        </Stack></Grid>
        <Grid size={{ xs: 12, xl: 4 }}><Card sx={{ position: 'sticky', top: 88 }}><CardContent>
          <Typography variant="h5">Cost summary</Typography><Divider sx={{ my: 2 }} />
          {calculate.data ? <Stack spacing={1.5}>
            {Object.entries(calculate.data).map(([key, value]) => <Stack key={key} direction="row" justifyContent="space-between">
              <Typography color={key === 'netProductCost' ? 'primary' : 'text.secondary'} fontWeight={key === 'netProductCost' ? 800 : 400}>
                {key.replace(/([A-Z])/g, ' $1').replace(/^./, (letter) => letter.toUpperCase())}</Typography>
              <Typography fontWeight={key === 'netProductCost' ? 800 : 600}>Rs {Number(value).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Typography>
            </Stack>)}
          </Stack> : <Typography color="text.secondary">Enter costs and calculate to see the full breakdown.</Typography>}
        </CardContent></Card></Grid>
      </Grid>
    </>
  );
}
